/**
 * Local investment wallet (BRIEF §12, §21).
 *
 * The reserve + destination wallet is a locally generated EVM account, kept in
 * IndexedDB and used only for small balances. The Gnosis Pay Safe is watched
 * read-only and is never controlled by this app. A later version should move
 * to passkey-secured smart accounts; for the MVP a burner keeps the loop
 * provable end-to-end.
 */
import { generatePrivateKey, privateKeyToAccount, type PrivateKeyAccount } from "viem/accounts";
import { getWalletKey, setWalletKey } from "./db";
import type { Hex } from "./types";

export interface LocalWallet {
  address: Hex;
  /** viem account used to sign live bridge/swap transactions. */
  account: PrivateKeyAccount;
  /** Present so Settings can offer an explicit reveal; never logged. */
  exportPrivateKey: () => Hex;
}

let cached: LocalWallet | null = null;

/**
 * Build a wallet from a pasted key WITHOUT persisting it — used to verify a
 * restore key actually owns a Gnosis Pay account before adopting it.
 */
export function walletFromKey(pk: string): LocalWallet {
  const key = pk.trim() as Hex;
  if (!/^0x[0-9a-fA-F]{64}$/.test(key)) {
    throw new Error("That doesn't look like a wallet key — expected 0x followed by 64 hex characters");
  }
  const account = privateKeyToAccount(key);
  return { address: account.address as Hex, account, exportPrivateKey: () => key };
}

/**
 * Replace the local wallet with a key exported from another device.
 * Gnosis Pay accounts are keyed to the wallet — not the email — so this is
 * how an account started elsewhere is resumed here. Safe pre-onboarding: the
 * generated wallet it replaces has never held funds.
 */
export async function importWalletKey(pk: string): Promise<LocalWallet> {
  const wallet = walletFromKey(pk); // validates
  await setWalletKey(wallet.exportPrivateKey());
  cached = null;
  return loadOrCreateWallet();
}

export async function loadOrCreateWallet(): Promise<LocalWallet> {
  if (cached) return cached;
  let pk = (await getWalletKey()) as Hex | undefined;
  if (!pk) {
    pk = generatePrivateKey();
    await setWalletKey(pk);
  }
  const account = privateKeyToAccount(pk);
  const key = pk;
  cached = {
    address: account.address as Hex,
    account,
    exportPrivateKey: () => key,
  };
  return cached;
}
